/**
 * Composable for anonymous authentication via seed phrase with key wrapping.
 *
 * Authentication flow:
 * 1. The user provides a 12-word BIP39 seed phrase.
 * 2. `useSeed.deriveCredentials` deterministically produces an anonymous email,
 *    a password, a legacy encryption key, and an AES-KW wrapper key from the seed.
 * 3. The composable attempts to sign in with the derived credentials.
 *    If the account doesn't exist yet, it automatically signs up instead.
 * 4. For new accounts: a random encryption key is generated, wrapped with the
 *    wrapper key, and stored on the server.
 * 5. For existing accounts: the wrapped key is downloaded from the server and
 *    unwrapped with the wrapper key.
 * 6. For legacy accounts (no wrapped key): the account is transparently migrated
 *    by re-encrypting all data with a new random key.
 *
 * No personal data (name, real email, etc.) is ever collected or transmitted.
 */
export function useAuth() {
  const supabase = useSupabaseClient()
  const user = useSupabaseUser()

  /**
   * Authenticates using a seed phrase.
   * Derives credentials and wrapper key from the seed, handles sign-in/sign-up,
   * and sets up the encryption key via key wrapping (or legacy migration).
   *
   * @returns `isNewUser` indicates whether a new account was created.
   */
  async function authenticate(
    seed: string,
    allowSignUp = true
  ): Promise<{
    isNewUser: boolean
    error: unknown
  }> {
    const { deriveCredentials } = useSeed()
    const { storeKeyInSession, storeLegacyKey } = useEncryption()
    const {
      setupNewAccountKey,
      retrieveEncryptionKey,
      migrateLegacyAccount,
      hasWrappedKeyOnServer
    } = useKeyWrapping()

    const normalizedSeed = seed.trim().toLowerCase().replace(/\s+/g, ' ')

    const { email, password, encryptionKey: legacyKey, wrapperKey } =
      await deriveCredentials(normalizedSeed)

    // Persist legacy key so sync can use it as fallback for data
    // still encrypted with the old seed-derived key (incomplete migration).
    await storeLegacyKey(legacyKey)

    // Try sign-in first
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (!signInError) {
      // Existing user — resolve encryption key
      try {
        const realKey = await resolveExistingUserKey(
          wrapperKey,
          legacyKey,
          hasWrappedKeyOnServer,
          retrieveEncryptionKey,
          migrateLegacyAccount
        )
        await storeKeyInSession(realKey)
        console.log('[auth] Encryption key resolved via key wrapping')
      } catch (e) {
        // Migration or key unwrap failed — fall back to legacy key so the
        // user can still log in. Migration will be retried on next login
        // once the server-side `user_keys` table is available.
        console.warn('[auth] Key resolution failed, using legacy key:', e)
        await storeKeyInSession(legacyKey)
      }
      return { isNewUser: false, error: null }
    }

    // If invalid credentials, treat as new user only when sign-up is allowed.
    // When allowSignUp is false (login tab), a wrong phrase is an error.
    const errorMessage =
      (signInError as { message?: string }).message?.toLowerCase() || ''

    if (
      errorMessage.includes('invalid login credentials') ||
      errorMessage.includes('invalid credentials')
    ) {
      if (!allowSignUp) {
        return { isNewUser: false, error: new Error('invalid_phrase') }
      }

      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password
      })

      if (signUpError) {
        return { isNewUser: false, error: signUpError }
      }

      // New user: generate random key, wrap it, upload
      try {
        const realKey = await setupNewAccountKey(wrapperKey)
        await storeKeyInSession(realKey)
        console.log('[auth] New account key generated and wrapped')
      } catch (e) {
        // Wrapped key upload failed (e.g. user_keys table missing) —
        // fall back to legacy key so the account can still be created.
        console.warn('[auth] Key setup failed, using legacy key:', e)
        await storeKeyInSession(legacyKey)
      }
      return { isNewUser: true, error: null }
    }

    // Other errors (network, rate limit, etc.)
    return { isNewUser: false, error: signInError }
  }

  /**
   * Resolves the encryption key for an existing user.
   * Checks if a wrapped key exists on the server. If yes, unwraps it.
   * If no, performs a legacy migration.
   */
  async function resolveExistingUserKey(
    wrapperKey: CryptoKey,
    legacyKey: CryptoKey,
    hasWrappedKeyOnServer: () => Promise<boolean>,
    retrieveEncryptionKey: (wk: CryptoKey) => Promise<CryptoKey | null>,
    migrateLegacyAccount: (lk: CryptoKey, wk: CryptoKey) => Promise<CryptoKey>
  ): Promise<CryptoKey> {
    const hasWrapped = await hasWrappedKeyOnServer()

    if (hasWrapped) {
      console.log('[auth] Wrapped key found on server, unwrapping')
      const realKey = await retrieveEncryptionKey(wrapperKey)
      if (!realKey) throw new Error('key_unwrap_failed')
      return realKey
    }

    // Legacy account — migrate transparently
    console.log('[auth] No wrapped key on server, migrating legacy account')
    return migrateLegacyAccount(legacyKey, wrapperKey)
  }

  /**
   * Signs out the current user and wipes all local key material.
   * Sync hooks and realtime subscriptions are cleaned up reactively by `useSync`
   * when it detects the user has been set to null.
   */
  async function signOut() {
    const { clearEncryptionKey, clearLegacyKey } = useEncryption()
    const { clearCachedWrappedKey } = useKeyWrapping()
    clearEncryptionKey()
    clearLegacyKey()
    clearCachedWrappedKey()
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  /**
   * Deletes the user's cloud account and all associated remote data.
   *
   * Inserts a row into `deletion_requests` with the user's ID. A database
   * trigger (`handle_deletion_request`) runs server-side with elevated
   * privileges to delete all user data and the `auth.users` entry.
   *
   * After the request is submitted, local data and encryption key are
   * cleared and the user is signed out.
   */
  async function deleteAccount() {
    if (!user.value) throw new Error('No authenticated user')

    const { error } = await supabase
      .from('deletion_requests')
      .insert({ request: true } as never)

    if (error) throw error

    const { clearEncryptionKey, clearLegacyKey } = useEncryption()
    const { clearCachedWrappedKey } = useKeyWrapping()
    clearEncryptionKey()
    clearLegacyKey()
    clearCachedWrappedKey()

    await supabase.auth.signOut()
  }

  return {
    user,
    authenticate,
    signOut,
    deleteAccount
  }
}
